import React from 'react';
import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { PointEconomy } from '../../../app/network/api/dashboard';

interface PointEconomyChartProps {
    data: PointEconomy[];
    loading?: boolean;
}

const PointEconomyChart: React.FC<PointEconomyChartProps> = ({ data, loading }) => {
    const theme = useTheme();

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        포인트 발행/사용 추이
                    </Typography>
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography color="text.secondary">로딩 중...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    포인트 발행/사용 추이
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                            <linearGradient id="colorIssued" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
                            </linearGradient>
                            <linearGradient id="colorUsed" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.8} />
                                <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0.1} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis
                            dataKey="date"
                            stroke={theme.palette.text.secondary}
                            style={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                                const date = new Date(value);
                                return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
                            }}
                        />
                        <YAxis stroke={theme.palette.text.secondary} style={{ fontSize: 12 }} />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 4,
                            }}
                        />
                        <Legend />
                        <Area
                            type="monotone"
                            dataKey="pointsIssued"
                            name="발행 포인트"
                            stroke={theme.palette.primary.main}
                            fillOpacity={1}
                            fill="url(#colorIssued)"
                            strokeWidth={2}
                        />
                        <Area
                            type="monotone"
                            dataKey="pointsUsed"
                            name="사용 포인트"
                            stroke={theme.palette.error.main}
                            fillOpacity={1}
                            fill="url(#colorUsed)"
                            strokeWidth={2}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default PointEconomyChart;
